import React, { useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import {
  Inventory as InventoryIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import InventoryMUI from './InventoryMUI'
import StockAdjustmentsEnhanced from './StockAdjustmentsEnhanced'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `inventory-tab-${index}`,
    'aria-controls': `inventory-tabpanel-${index}`,
  }
}

interface InventoryManagementTabsProps {
  defaultTab?: 'inventory' | 'adjustments'
}

const InventoryManagementTabs: React.FC<InventoryManagementTabsProps> = ({ 
  defaultTab = 'inventory' 
}) => {
  const tabIndexMap = {
    inventory: 0,
    adjustments: 1,
  }

  const [activeTab, setActiveTab] = useState(tabIndexMap[defaultTab])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [pendingTab, setPendingTab] = useState<number | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (hasUnsavedChanges) {
      setPendingTab(newValue)
      setShowConfirmDialog(true)
    } else {
      setActiveTab(newValue)
    }
  }

  const handleConfirmTabChange = () => {
    if (pendingTab !== null) {
      setActiveTab(pendingTab)
      setHasUnsavedChanges(false)
      setPendingTab(null)
    }
    setShowConfirmDialog(false)
  }

  const handleCancelTabChange = () => {
    setPendingTab(null)
    setShowConfirmDialog(false)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="inventory management tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              minHeight: 64,
            },
            '& .Mui-selected': {
              fontWeight: 600,
            },
          }}
        >
          <Tab
            icon={<InventoryIcon />}
            iconPosition="start"
            label="Inventory Levels"
            {...a11yProps(0)}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Stock Adjustments"
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <InventoryMUI />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <StockAdjustmentsEnhanced />
      </TabPanel>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelTabChange}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmTabChange} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InventoryManagementTabs
